import { RootState } from ".";

import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { getIntegrations } from "../api/integration";
import { getSuppressionAudienceMetadata } from "../api/suppression-audience";
import { IntegrationSchema } from "../types/integration";
import { SuppressionAudienceField } from "../types/suppression-audience";
import { Workspace } from "../types/workspace";

export interface WorkspaceState {
  isLoadingIntegrations: boolean;
  isLoadingSuppressionAudienceFields: boolean;
  integrations: IntegrationSchema[];
  suppressionAudienceFields: SuppressionAudienceField[];
  slug: string | null;
}

export const loadIntegrations = createAsyncThunk<
  IntegrationSchema[],
  void,
  { state: RootState }
>("workspace/loadIntegrations", async (_, { getState }) => {
  const {
    workspace: { slug },
  } = getState();
  return await getIntegrations(slug);
});

export const loadSuppressionAudienceFields = createAsyncThunk<
  SuppressionAudienceField[],
  void,
  { state: RootState }
>("workspace/loadSuppressionAudienceFields", async (_, { getState }) => {
  const {
    workspace: { slug },
  } = getState();
  const metadata = await getSuppressionAudienceMetadata(slug);
  return metadata.fields;
});

export const selectWorkspace = (state: RootState) => state.workspace;
export const selectFacebookIntegration = createSelector(
  [selectWorkspace],
  (workspace) => workspace.integrations.find((i) => i.id === "facebook"),
);
export const selectCurrentWorkspace = createSelector(
  [(state: RootState) => state.auth.user?.workspaces, selectWorkspace],
  (workspaces, workspace): Workspace => {
    const currentWorkspace = workspaces?.find((w) => w.slug === workspace.slug);
    if (!currentWorkspace) {
      throw new Error(`Workspace with slug "${workspace.slug}" not found`);
    }
    return currentWorkspace;
  },
);

export const workspaceSlice = createSlice({
  name: "workspace",
  initialState: {
    isLoadingIntegrations: false,
    isLoadingSuppressionAudienceFields: false,
    integrations: [],
    suppressionAudienceFields: [],
    slug: null,
  } as WorkspaceState,
  reducers: {
    setSlug: (state, action: PayloadAction<string>) => {
      state.slug = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadIntegrations.pending, (state) => {
      state.isLoadingIntegrations = true;
    });
    builder.addCase(loadIntegrations.fulfilled, (state, action) => {
      state.isLoadingIntegrations = false;
      state.integrations = action.payload;
    });
    builder.addCase(loadIntegrations.rejected, (state) => {
      state.isLoadingIntegrations = false;
    });
    builder.addCase(loadSuppressionAudienceFields.pending, (state) => {
      state.isLoadingSuppressionAudienceFields = true;
    });
    builder.addCase(
      loadSuppressionAudienceFields.fulfilled,
      (state, action) => {
        state.isLoadingSuppressionAudienceFields = false;
        state.suppressionAudienceFields = action.payload;
      },
    );
    builder.addCase(loadSuppressionAudienceFields.rejected, (state) => {
      state.isLoadingSuppressionAudienceFields = false;
    });
  },
});

export const { setSlug } = workspaceSlice.actions;

export default workspaceSlice.reducer;
